import { IconAdd } from "@tine/designsystem-icons/sharp";
import { Checkbox, Group, Stack } from "@tine/designsystem-ui-react";
import { keyBy, mapValues, sortBy, toLower, values } from "lodash-es";
import { observer } from "mobx-react-lite";
import { type FormEvent, useState } from "react";
import { logger } from "../../common/logger";
import type { FormValuesToSubmit } from "../../common/types/formTypes";
import Button from "../../components/Button";
import ExpandableList from "../../components/ExpandableList";
import { validateFavoriteListName } from "../../components/formvalidation/FormUtils/FieldValidators";
import ValidatedForm from "../../components/formvalidation/ValidatedForm";
import ValidatedInput from "../../components/formvalidation/ValidatedInput";
import Modal from "../../components/Modal";
import cartStore from "../../stores/cart/cartStore";
import favoriteStore, { type FavoriteList } from "../../stores/favoriteStore";
import toastStore from "../../stores/toastStore";

type AddToFavoriteProps = {
   isOpen: boolean;
   onCloseModal: () => void;
   favoriteListAdded: () => void;
};

const textMuted = "text-muted";

const AddToFavoriteModal = ({ isOpen = false, onCloseModal, favoriteListAdded }: AddToFavoriteProps) => {
   const [mode, setMode] = useState<"newList" | "existingList">("existingList");
   const favoriteLists = sortBy(values(favoriteStore.allFavoriteLists), (l) => toLower(l.listName));

   const onClose = () => {
      onCloseModal();
      setMode("existingList");
   };

   if (mode === "existingList") {
      return (
         <AddToExistingList
            favoriteLists={favoriteLists}
            onClose={onClose}
            onAddedToList={favoriteListAdded}
            setMode={setMode}
            isOpen={isOpen}
         />
      );
   }

   return <AddNewList onClose={onClose} onNewListAdded={favoriteListAdded} isOpen={isOpen} />;
};

type AddNewListProps = {
   onClose: () => void;
   isOpen: boolean;
   onNewListAdded: () => void;
};

const AddNewList = observer(({ onClose, isOpen, onNewListAdded }: AddNewListProps) => {
   const cartItems = cartStore.items;
   const favoriteItems: Record<string, number> = mapValues(keyBy(cartItems, "sku"), "qty");
   const skus = cartItems.map(({ sku }) => sku);
   const qtySum = cartItems.reduce((prev, item) => prev + item.qty, 0);

   const handleSubmit = async (data: FormValuesToSubmit) => {
      try {
         if (data.listName === undefined) return {};

         const list = await favoriteStore.createNewListInStorage(data.listName as string);
         toastStore.addSuccess("Opprettet", "Favorittlisten har blitt opprettet.");

         if (typeof list === "object") {
            const listId = favoriteStore.getFavoriteListByName(data.listName as string)?.id;
            if (listId) {
               favoriteStore.addMultipleFavorites(skus, list.id, favoriteItems);
            }
         }
         onNewListAdded();
         onClose();
         return { resetFormFields: true };
      } catch (err) {
         logger.error("favorites", "Error creating favorite list:", err);
         toastStore.addError("Det oppsto en feil", "Kunne ikke opprette favorittlisten. Prøv igjen senere.");
         return { resetFormFields: false };
      }
   };

   return (
      <Modal title="Lagre ny favorittliste" isOpen={isOpen} name="Legg til i favorittliste" zIndex={1050}>
         <ValidatedForm onSubmit={handleSubmit}>
            <ValidatedInput
               type="text"
               initialValue={""}
               name="listName"
               label=""
               placeholder="Navn på listen"
               validator={validateFavoriteListName}
               data-testid="add-new-favorite-list-from-cart-input"
               required
            />
            <small className={textMuted}>
               Du ønsker å legge til {qtySum} produkter – {skus.length} varelinje(r) – i denne favorittlisten.
            </small>
            <Group justify="end" gap={1} className="mt-6">
               <Button variant="secondary" className="me-2" onClick={onClose}>
                  Avbryt
               </Button>
               <Button variant="primary" type="submit" data-testid="save-new-favorite-list-from-cart-button">
                  Lagre
               </Button>
            </Group>
         </ValidatedForm>
      </Modal>
   );
});

type AddToExistingListProps = {
   favoriteLists: FavoriteList[];
   onClose: () => void;
   onAddedToList: () => void;
   setMode: (newMode: "newList" | "existingList") => void;
   isOpen: boolean;
};

const AddToExistingList = observer(({ favoriteLists, onAddedToList, setMode, isOpen, onClose }: AddToExistingListProps) => {
   const cartItems = cartStore.items;
   const skus = cartItems.map(({ sku }) => sku);
   const favoriteItems: Record<string, number> = mapValues(keyBy(cartItems, "sku"), "qty");
   const [checkedLists, setCheckedLists] = useState<string[]>([]);

   const onCheckList = (listId: string) => {
      setCheckedLists([...checkedLists, listId]);
   };
   const onUncheckList = (listId: string) => {
      const newList = checkedLists.filter((id) => id !== listId);
      setCheckedLists(newList);
   };

   const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      checkedLists.map((listId) => {
         favoriteStore.addMultipleFavorites(skus, listId, favoriteItems);
      });
      onAddedToList();
      onClose();
      toastStore.addSuccess("Lagret", "Favorittlisten har blitt oppdatert med nye produkter.");
   };

   return (
      <Modal isOpen={isOpen} onClose={onClose} title="Lagre som favorittliste" name="Legg til i favorittliste" zIndex={1050}>
         <Stack align="center" gap={0} className="mb-6">
            <Button
               size="regular"
               variant="tertiary"
               icon={<IconAdd />}
               onClick={() => setMode("newList")}
               data-testid="create-new-favorite-list-from-cart-button"
            >
               Lag ny liste
            </Button>
            <small className={textMuted}>Opprett en ny liste med de valgte varene</small>
         </Stack>
         {favoriteLists.length > 0 && (
            <div className="px-0 py-2 md:px-3">
               <div className="mb-3">
                  <h2 className="m-0">Legg til i eksisterende liste</h2>
                  <small className={textMuted}>Velg listen du ønsker å legge varene i.</small>
               </div>

               <form onSubmit={handleSubmit}>
                  <Stack align="start">
                     <div className="p-0 m-0 overflow-hidden">
                        <ExpandableList
                           cutoff={5}
                           items={favoriteLists.map((list) => (
                              <div key={`add-to-favorite-list-${list.id}`}>
                                 <Checkbox
                                    label={list.listName}
                                    onChange={(event) => (event.target.checked ? onCheckList(list.id) : onUncheckList(list.id))}
                                    isChecked={checkedLists?.includes(list.id)}
                                    className="mb-1"
                                 />
                              </div>
                           ))}
                        />
                     </div>

                     <Group justify="end" align="end" gap={2} className="w-full">
                        <Button variant="primary" type="submit" disabled={checkedLists.length === 0}>
                           Lagre
                        </Button>
                        <Button variant="secondary" onClick={onClose}>
                           Avbryt
                        </Button>
                     </Group>
                  </Stack>
               </form>
            </div>
         )}
      </Modal>
   );
});

export default observer(AddToFavoriteModal);
